import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV } from "@/content/site";
import { useInsightsMeta } from "@/hooks/useInsightsMeta";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const insights = useInsightsMeta();
  const navItems = insights.has_published ? [...NAV, { label: "Insights", to: "/insights" }] : NAV;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-azeka-navy/95 backdrop-blur-xl shadow-lg shadow-azeka-navy/20"
          : "bg-azeka-navy/85 backdrop-blur-md"
      } border-b border-white/10`}
    >
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Link to="/" data-testid="logo-home-link" className="flex items-center shrink-0">
          <Logo onDark className="h-9 w-auto" />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors link-underline ${
                  isActive ? "text-azeka-teal2" : "text-slate-200 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-azeka-teal px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-azeka-teal2 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-azeka-teal2 focus:ring-offset-2 focus:ring-offset-azeka-navy"
          >
            Make an Enquiry
            <ArrowRight className="h-4 w-4" />
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
                className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-white hover:bg-white/10"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm border-l-0 bg-azeka-navy p-0 text-white">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">Azeka Global site navigation menu</SheetDescription>
              <div className="flex items-center justify-between px-6 h-[72px] border-b border-white/10">
                <Logo onDark className="h-8 w-auto" />
                <SheetClose asChild>
                  <button aria-label="Close menu" className="inline-flex h-10 w-10 items-center justify-center rounded-lg hover:bg-white/10">
                    <X className="h-6 w-6" />
                  </button>
                </SheetClose>
              </div>
              <nav className="flex flex-col px-4 py-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-nav-${item.to.replace("/", "")}`}
                    className={({ isActive }) =>
                      `rounded-lg px-4 py-3.5 text-base font-medium transition-colors ${
                        isActive ? "bg-white/10 text-azeka-teal2" : "text-slate-200 hover:bg-white/5 hover:text-white"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Link
                  to="/contact"
                  data-testid="mobile-cta-contact"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-azeka-teal px-5 py-3.5 text-base font-semibold text-white hover:bg-azeka-teal2"
                >
                  Make an Enquiry <ArrowRight className="h-4 w-4" />
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
